import * as Sentry from "@sentry/react";

type TelemetryPrimitive = string | number | boolean | null | undefined;
type TelemetryAttributeValue = TelemetryPrimitive | TelemetryPrimitive[];
type TelemetryAttributes = Record<string, TelemetryAttributeValue>;
type TelemetryMeasurementUnit = Parameters<typeof Sentry.setMeasurement>[2];
type TelemetryMetricUnit = NonNullable<Parameters<typeof Sentry.metrics.distribution>[2]>["unit"];

/**
 * Represents a collection of telemetry measurements, where each key is a metric name
 * mapped to its numeric value and unit.
 */
export type TelemetryMeasurements = Record<
  string,
  {
    value: number;
    unit: TelemetryMeasurementUnit;
  }
>;

/**
 * Configuration options for applying tags and attributes to a specific telemetry scope.
 */
export type TelemetryScopeOptions = {
  /** Optional tags to apply to the scope, which are indexed and searchable. */
  tags?: TelemetryAttributes;
  /** Optional attributes for metrics recorded within this scope. */
  metricAttributes?: TelemetryAttributes;
};

function normalizeAttributeValue(value: TelemetryAttributeValue): string | number | boolean | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== null && entry !== undefined)
      .map((entry) => String(entry))
      .join(",");
  }

  return value;
}

function normalizeAttributes(attributes: TelemetryAttributes = {}): Record<string, string | number | boolean> {
  return Object.entries(attributes).reduce<Record<string, string | number | boolean>>((acc, [key, value]) => {
    const normalized = normalizeAttributeValue(value);
    if (normalized !== undefined) {
      acc[key] = normalized;
    }
    return acc;
  }, {});
}

function applyScopeOptions(scope: ReturnType<typeof Sentry.getCurrentScope>, options: TelemetryScopeOptions = {}): void {
  const normalizedTags = normalizeAttributes(options.tags);
  const normalizedMetricAttributes = normalizeAttributes(options.metricAttributes);

  if (Object.keys(normalizedTags).length > 0) {
    scope.setTags(normalizedTags);
  }

  if (Object.keys(normalizedMetricAttributes).length > 0) {
    scope.setAttributes(normalizedMetricAttributes);
  }
}

/**
 * Annotates the currently active telemetry span with additional attributes and measurements.
 * 
 * @param attributes - Key-value pairs of attributes to attach to the span.
 * @param measurements - A collection of measurements (values with units) to record on the span.
 * @returns void
 * 
 * @sideEffects Mutates the active Sentry span if one exists.
 */
export function annotateActiveSpan(
  attributes: TelemetryAttributes = {},
  measurements: TelemetryMeasurements = {},
): void {
  const activeSpan = Sentry.getActiveSpan();

  if (activeSpan) {
    activeSpan.setAttributes(normalizeAttributes(attributes));

    Object.entries(measurements).forEach(([name, measurement]) => {
      Sentry.setMeasurement(name, measurement.value, measurement.unit, activeSpan);
    });
  }
}

/**
 * Records a generic count metric to track the frequency of an event.
 * 
 * @param name - The identifier for the count metric.
 * @param value - The amount to increment the counter by. Defaults to 1.
 * @param attributes - Optional attributes to associate with the metric.
 * @returns void
 */
export function recordCountMetric(
  name: string,
  value = 1,
  attributes: TelemetryAttributes = {},
): void {
  Sentry.metrics.count(name, value, {
    attributes: normalizeAttributes(attributes),
  });
}

/**
 * Records a distribution metric to track a value over time, such as latency or payload size.
 * 
 * @param name - The identifier for the distribution metric.
 * @param value - The numeric value to record.
 * @param unit - The unit of measurement (e.g., "millisecond", "byte").
 * @param attributes - Optional attributes to associate with the metric.
 * @returns void
 */
export function recordDistributionMetric(
  name: string,
  value: number,
  unit: TelemetryMetricUnit,
  attributes: TelemetryAttributes = {},
): void {
  const options = {
    attributes: normalizeAttributes(attributes),
    ...(unit ? { unit } : {}),
  };

  Sentry.metrics.distribution(name, value, options);
}

/**
 * Wraps an asynchronous operation within a new telemetry span, capturing its duration and status.
 * Applies any provided scope options (tags, attributes) specifically for the duration of the callback.
 * 
 * @param name - The descriptive name of the span (e.g., "fetchUserProfile").
 * @param op - The operation type category (e.g., "http.client", "db.query").
 * @param attributes - Attributes to attach to the newly created span.
 * @param callback - The asynchronous function to execute and measure.
 * @param options - Additional scope configuration like tags and metric attributes.
 * @returns A promise resolving to the result of the callback function.
 */
export async function withTelemetrySpan<T>(
  name: string,
  op: string,
  attributes: TelemetryAttributes,
  callback: () => Promise<T>,
  options: TelemetryScopeOptions = {},
): Promise<T> {
  return Sentry.withScope(async (scope) => {
    applyScopeOptions(scope, options);

    return Sentry.startSpan(
      {
        name,
        op,
        attributes: normalizeAttributes(attributes),
      },
      async () => {
        annotateActiveSpan(attributes);

        return callback();
      },
    );
  });
}
